import { html, LitElement, nothing } from 'lit';
import { stratum } from './constants/stratum.js';
import './components/login/login-component/login.component.js';
import { DebugService } from './services/debug/debug.service.js';
import { globalStyle } from './global-styles.style.js';
import { REFI_WEB_UI_CONST, MARKETING_CONST, CURRENT_STEP } from './constants/constants.js';
import SessionService from './services/session/session.service.js';
import { QUERY_PARAMS } from './constants/login.constants.js';
import { CookieService } from './services/cookie/cookie.service.js';
import { HostAppEventsService } from './utils/host-app-events-service.js';

class RefiUserEntryMFE extends LitElement {
  constructor() {
    super();
    this.initProperties();
    this.showMfeContainer = false;
    this.error = null;
    this.debug = new DebugService('RefiUserEntryMFEApp');
    this.showSignOut = false;
    const params = new URLSearchParams(window.location.search);
    const externalId = params.get(QUERY_PARAMS.EXTERNAL_ID);
    if (externalId && externalId !== 'null') {
      SessionService.setExternalIdInSession(externalId);
    }
  }

  static styles = [globalStyle];

  static properties = {
    isLoading: { type: Boolean, reflect: true },
    currentStep: { type: String, attribute: 'current-step', reflect: true },
    experiments: { type: Object, attribute: 'experiments', reflect: true },
    appList: { type: Array, attribute: 'app-list', reflect: true },
    applicantFirstName: { type: String, attribute: 'applicant-first-name', reflect: true },
    featureToggles: { type: Object, attribute: 'feature-toggles', reflect: true },
  };

  initProperties() {
    this.appList = [];
    this.currentStep = '';
    this.isLoading = true;
  }

  connectedCallback() {
    super.connectedCallback();

    this.debug.log('currentStep:', this.currentStep);
    this.debug.log('appList:', this.appList);
    if (!this.currentStep) {
      this.debug.log('no current step received');
      if (this.appList && this.appList.length > 0) {
        this.debug.log('appList received:', this.appList);
        HostAppEventsService.sendNextStepEvent({ step: 'pick-an-app' });
      } else {
        HostAppEventsService.sendNextStepEvent({ step: 'login' });
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  updated(changes) {
    super.updated(changes);
  }

  render() {
    const mainContent = this.renderPage();
    return html` <div>${this.isLoading ? html`<refi-loading-spinner message="Loading..."></refi-loading-spinner>` : nothing} ${mainContent}</div> `;
  }

  renderPage() {
    this.debug.log('in render page function, currentStep=', this.currentStep);
    switch (this.currentStep) {
      case CURRENT_STEP.LOGIN:
      case CURRENT_STEP.LOGIN_:
        return html`<refi-login .featureToggles="${this.featureToggles}"></refi-login>`;
      case CURRENT_STEP.OFFER:
        return html`<refi-offer .featureToggles="${this.featureToggles}"></refi-offer>`;
      case CURRENT_STEP.EASE_OFFER:
        this.easeOfferGuard();
        return html`<refi-ease-offer ?has-apps="${this.appList.length > 0}"></refi-ease-offer>`;
      case CURRENT_STEP.PICK_AN_APP:
        if (this.appList.length >= 1) {
          return html`<refi-pick-an-app-v2 .apps="${this.appList}" applicant-first-name="${this.applicantFirstName}" .featureToggles="${this.featureToggles}" .experiments="${this.experiments}"></refi-pick-an-app-v2>`;
        }
        HostAppEventsService.goToLogin();
        return null;
      case CURRENT_STEP.CODELESS_ENTRY:
      case CURRENT_STEP.CODELESS_ENTRY_:
        this.easeOfferGuard();
        return html`<refi-codeless-entry ?has-apps="${this.appList.length > 0}"></refi-codeless-entry>`;
      default: {
        this.debug.log('default case, no current step and loading nothing');
        return null;
      }
    }
  }

  async firstUpdated() {
    this.debug.log('first updated app component');
    this.persistMarketingAttributes();
    this.isLoading = false;
    HostAppEventsService.hideLoaderSpinnerEvent();
  }

  persistMarketingAttributes() {
    const params = new URLSearchParams(window.location.search);
    const containsMarketingAttributes = params.has(QUERY_PARAMS.PFFSRCID) || params.has(QUERY_PARAMS.EXTERNAL_ID);
    if (containsMarketingAttributes) {
      let afw3CookieVal;
      // Check to see if we don't already have an afw3 cookie
      // and if we do, ensure that it's not BAU so that the earliest non-BAU one persists
      if (CookieService.getCookie(REFI_WEB_UI_CONST.MARKETING_ATTRIBUTE_COOKIE) !== undefined && !CookieService.getCookie(REFI_WEB_UI_CONST.MARKETING_ATTRIBUTE_COOKIE)?.includes(MARKETING_CONST.COAF_EXTERNAL_ID_DEFAULT_VAL)) {
        afw3CookieVal = CookieService.getCookie(REFI_WEB_UI_CONST.MARKETING_ATTRIBUTE_COOKIE);
      } else {
        let referralSource = params.get(QUERY_PARAMS.PFFSRCID);
        referralSource = referralSource !== null ? referralSource : MARKETING_CONST.COAF_PFFSOURCEID_DEFAULT_VAL;

        let externalId = params.get(QUERY_PARAMS.EXTERNAL_ID);
        externalId = externalId !== null ? externalId : MARKETING_CONST.COAF_EXTERNAL_ID_DEFAULT_VAL;

        this.debug.log('referralSource:', referralSource);
        this.debug.log('externalId:', externalId);

        // Construct the cookie value
        afw3CookieVal = [
          QUERY_PARAMS.EXTERNAL_ID,
          MARKETING_CONST.DELIM_CARET,
          externalId,
          MARKETING_CONST.DELIM_PIPE,
          MARKETING_CONST.EXTERNAL_ID_PLATFORM_IDENTIFIER,
          MARKETING_CONST.DELIM_CARET,
          MARKETING_CONST.COAF_ANDROMEDA,
          MARKETING_CONST.DELIM_PIPE,
          MARKETING_CONST.COAF_PFFSOURCEID,
          MARKETING_CONST.DELIM_CARET,
          referralSource,
          MARKETING_CONST.DELIM_PIPE,
          MARKETING_CONST.PFFSRCID_PLATFORM_IDENTIFIER,
          MARKETING_CONST.DELIM_CARET,
          MARKETING_CONST.COAF_ANDROMEDA,
          MARKETING_CONST.DELIM_PIPE,
        ].join('');
      }
      CookieService.createOrUpdateCookie(REFI_WEB_UI_CONST.MARKETING_ATTRIBUTE_COOKIE, afw3CookieVal, MARKETING_CONST.COOKIE_SECURE, MARKETING_CONST.COOKIE_DOMAIN, MARKETING_CONST.COOKIE_MAX_AGE, MARKETING_CONST.COOKIE_PATH);
      this.debug.log('persist marketing attributes update successful, new cookie val: ', afw3CookieVal);
      this.updateStratumMarketingChannel();
    }
  }

  async easeOfferGuard() {
    return new Promise(res => {
      this.showSignOut = false;
      this.showBack = false;
      res();
    });
  }

  updateStratumMarketingChannel() {
    const parsedCookie = CookieService.getCookie(REFI_WEB_UI_CONST.MARKETING_ATTRIBUTE_COOKIE)?.split(/\^|\|/);
    const externalIdFromCookie = parsedCookie?.[1]; // oldest non-bau attribution
    if (parsedCookie?.[0] === 'external_id') {
      // sets var to query param external id if first time with offer code, otherwise cookie
      stratum.setExternalVar('marketingChannel', externalIdFromCookie || SessionService.getExternalId());
    }
  }
}

customElements.define('refi-user-entry-mfe', RefiUserEntryMFE);
